import React from 'react';
import clsx from 'clsx';
import styled from 'styled-components';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ALabel, ALabelSize, ADigitSize } from '@marriott/mi-web-ui-kit';
import { baseVariables, toRem } from '@marriott/mi-ui-library';
import { ADigit } from '@marriott/mi-web-ui-kit';

const StyledMathEquation = styled.div<{ $isInverse?: boolean }>`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: ${toRem(16)};
  span {
    color: ${({ $isInverse }) =>
      $isInverse ? baseVariables.color['base10'] : baseVariables.color['base20']} !important;
  }

  > *:first-child {
    padding-right: ${toRem(8)};
  }

  > *:not(:first-child):not(:last-child) {
    padding-left: ${toRem(8)};
    padding-right: ${toRem(8)};
  }

  > *:last-child {
    @media ${baseVariables.mediaQuery.md} {
      padding-left: ${toRem(8)};
    }
  }
`;

const StyledDivider = styled.div<{ $isInverse?: boolean }>`
  flex-basis: 100%;
  height: ${toRem(2)};
  margin: ${toRem(8)} 0;
  background-color: ${({ $isInverse }) =>
    $isInverse ? baseVariables.color['base10'] : baseVariables.color['base20']} !important;

  @media ${baseVariables.mediaQuery.md} {
    display: none;
  }
`;

const StyledTotalColumn = styled.div`
  flex-basis: 100%;
  padding-left: 0;

  @media ${baseVariables.mediaQuery.md} {
    flex-basis: auto;
    padding-left: ${toRem(8)};
  }
`;

interface MathEquationProps {
  children: React.ReactNode;
  className?: string;
  isInverse?: boolean;
  'aria-label'?: string;
}

export function MathEquation({ children, className, isInverse, 'aria-label': ariaLabel }: MathEquationProps) {
  return (
    <StyledMathEquation
      className={className}
      $isInverse={isInverse}
      data-testid="cobrand-math-section"
      role={ariaLabel ? 'group' : undefined}
      aria-label={ariaLabel}
    >
      {children}
    </StyledMathEquation>
  );
}

interface EqColumnProps {
  label: string;
  value: string;
  isTotal?: boolean;
  isInverse: boolean;
}

export function EqColumn({ label, value, isTotal, isInverse }: EqColumnProps) {
  const content = (
    <div className="d-flex flex-column gap-1">
      <ALabel
        size={ALabelSize.S}
        text={label}
        className={clsx(isInverse && 't-label-inverse-s', 't-font-weight-r', 'pb-2')}
      />
      <EqValue value={value} isInverse={isInverse} />
    </div>
  );

  // Wrap total column in StyledTotalColumn with divider above to force it to second row on mobile
  if (isTotal) {
    return (
      <StyledTotalColumn>
        <StyledDivider $isInverse={isInverse} />
        {content}
      </StyledTotalColumn>
    );
  }

  return content;
}

interface EqValueProps {
  value: string;
  isInverse: boolean;
}

export function EqValue({ value, isInverse }: EqValueProps) {
  return (
    <>
      <ADigit
        size={ADigitSize.Medium}
        text={value}
        className={clsx('d-md-none', isInverse ? 't-title-inverse-xs' : 't-title-xs')}
      />
      <ADigit
        size={ADigitSize.Medium}
        text={value}
        className={clsx('d-none d-md-block', isInverse ? 't-title-numbers-s' : 't-numbers-s')}
      />
    </>
  );
}

interface EqOperatorProps {
  operator: '+' | '=';
  isInverse: boolean;
}

export function EqOperator({ operator, isInverse }: EqOperatorProps) {
  return (
    <>
      <ADigit
        size={ADigitSize.Medium}
        text={operator}
        className={clsx('align-self-end px-1 px-md-2 d-md-none', isInverse ? 't-title-inverse-xs' : 't-title-xs')}
      />
      <ADigit
        size={ADigitSize.Medium}
        text={operator}
        className={clsx(
          'align-self-end px-1 px-md-2 d-none d-md-block',
          isInverse ? 't-numbers-inverse-s' : 't-numbers-s'
        )}
      />
    </>
  );
}
